<script setup lang="ts">
import axios from 'axios'
import dayjs, { type Dayjs } from 'dayjs'
import { Check, ChartColumn, Copy, ExternalLink, Minus, PlugZap, TrendingDown, TrendingUp } from 'lucide-vue-next'

import { API_DOC_URL } from '@/constants/apiKeys'
import type { ApiKeyToken, ApiKeyUsageDay } from '@/types/apiKeys'

const PERIODS = ['7', '30', 'month'] as const
type Period = (typeof PERIODS)[number]

const props = defineProps<{
  open: boolean
  token: ApiKeyToken | null
}>()

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
}>()

const { t } = useI18n()
const { formatNumber } = useNumber()
const { formatDate } = useDate()
const { copyToClipboard } = useDOM()
const notification = useNotification()

const isLoading = ref(false)
const days = ref<ApiKeyUsageDay[]>([])
const period = ref<Period>('30')
const snippetCopied = ref(false)

const fetchUsage = async () => {
  if (!props.token) return
  isLoading.value = true
  days.value = []
  try {
    const { data } = await axios.get<{ data: ApiKeyUsageDay[] }>(`/api-keys/${props.token.id}/usage`)
    days.value = data.data
  } catch {
    notification.create('error', { text: t('pages.settings.apiKeys.errors.usage') })
  } finally {
    isLoading.value = false
  }
}

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      period.value = '30'
      snippetCopied.value = false
      fetchUsage()
    }
  },
  { immediate: true }
)

const today = computed(() => (days.value.length ? dayjs(days.value[days.value.length - 1].date) : dayjs()))
const byDate = computed(() => new Map(days.value.map((d) => [d.date, d.request_count])))
const valueAt = (date: Dayjs) => byDate.value.get(date.format('YYYY-MM-DD')) ?? 0

const periodTabs = computed(() =>
  PERIODS.map((value) => ({ value, name: t(`pages.settings.apiKeys.usage.periods.${value}`) }))
)

const range = computed(() => {
  if (period.value === 'month') {
    const start = today.value.startOf('month')
    const dates = Array.from({ length: today.value.daysInMonth() }, (_, i) => start.add(i, 'day'))
    const prevStart = today.value.subtract(1, 'month').startOf('month')
    const prevDates = Array.from({ length: today.value.date() }, (_, i) => prevStart.add(i, 'day'))
    return {
      dates,
      prevDates,
      rangeLabel: t('pages.settings.apiKeys.usage.rangeSince', { from: start.format('DD/MM') })
    }
  }

  const n = Number(period.value)
  const dates = Array.from({ length: n }, (_, i) => today.value.subtract(n - 1 - i, 'day'))
  const prevDates = Array.from({ length: n }, (_, i) => today.value.subtract(2 * n - 1 - i, 'day'))
  return {
    dates,
    prevDates,
    rangeLabel: t('pages.settings.apiKeys.usage.rangeBetween', {
      from: dates[0].format('DD/MM'),
      to: today.value.format('DD/MM')
    })
  }
})

const pastDates = computed(() => range.value.dates.filter((d) => !d.isAfter(today.value, 'day')))
const pastValues = computed(() => pastDates.value.map(valueAt))
const total = computed(() => pastValues.value.reduce((sum, v) => sum + v, 0))
const average = computed(() => (pastValues.value.length ? Math.round(total.value / pastValues.value.length) : 0))
const hasData = computed(() => total.value > 0)

const peakIndex = computed(() => {
  if (!hasData.value) return -1
  let idx = 0
  for (let i = 1; i < pastValues.value.length; i++) {
    if (pastValues.value[i] > pastValues.value[idx]) idx = i
  }
  return idx
})

const peakLabel = computed(() => {
  if (peakIndex.value === -1) return '—'
  const date = pastDates.value[peakIndex.value]
  return `${date.format('ddd DD/MM')} · ${formatNumber(pastValues.value[peakIndex.value])}`
})

const prevTotal = computed(() => range.value.prevDates.reduce((sum, d) => sum + valueAt(d), 0))
const delta = computed(() =>
  prevTotal.value > 0 ? Math.round(((total.value - prevTotal.value) / prevTotal.value) * 100) : null
)
const hasDelta = computed(() => hasData.value && delta.value !== null)
const deltaLabel = computed(() => {
  if (delta.value === null) return ''
  const value = delta.value > 0 ? `+${delta.value}` : `${delta.value}`
  return t('pages.settings.apiKeys.usage.delta', { value })
})
const deltaIcon = computed(() => (!delta.value ? Minus : delta.value > 0 ? TrendingUp : TrendingDown))
const deltaClass = computed(() => {
  if (!delta.value) return 'text-muted-foreground'
  return delta.value > 0 ? 'text-green-600' : 'text-red-600'
})

const neverUsed = computed(() => !!props.token && !props.token.lastUsedAt)

const lastUsedLabel = computed(() => {
  if (!props.token?.lastUsedAt) return t('pages.settings.apiKeys.list.neverUsed')
  return formatDate({ date: props.token.lastUsedAt, toFormat: 'DD/MM/YYYY' })
})

const noDataHint = computed(() => {
  const token = props.token
  if (!token) return ''
  if (token.status === 'revoked') return t('pages.settings.apiKeys.usage.noData.hintRevoked')
  if (token.status === 'expired' && token.expiresAt) {
    return t('pages.settings.apiKeys.usage.noData.hintExpired', {
      date: formatDate({ date: token.expiresAt, toFormat: 'DD/MM/YYYY' })
    })
  }
  if (token.lastUsedAt) {
    return t('pages.settings.apiKeys.usage.noData.hintLastCall', {
      date: formatDate({ date: token.lastUsedAt, toFormat: 'DD/MM/YYYY' })
    })
  }
  return ''
})

const snippet = computed(
  () => `curl https://api.pongo.app/v1/customers \\\n  -H "Authorization: Bearer ${props.token?.prefix ?? ''}"`
)

const copySnippet = async () => {
  await copyToClipboard(snippet.value)
  snippetCopied.value = true
}

const categories = computed(() => {
  const dates = range.value.dates
  const n = dates.length
  return dates.map((date, i) => {
    if (period.value === 'month') return i === 0 || (i + 1) % 5 === 0 ? String(i + 1) : ''
    if (n <= 7) return date.format('DD/MM')
    return (n - 1 - i) % 7 === 0 ? date.format('DD/MM') : ''
  })
})

const series = computed(() => [
  {
    name: t('pages.settings.apiKeys.usage.callsSeriesName'),
    data: range.value.dates.map((date) => (date.isAfter(today.value, 'day') ? null : valueAt(date)))
  }
])

const chartOptions = computed(() => ({
  chart: {
    type: 'bar' as const,
    height: 220,
    fontFamily: 'inherit',
    toolbar: { show: false },
    zoom: { enabled: false }
  },
  colors: ['#2a0122'],
  plotOptions: {
    bar: {
      borderRadius: 3,
      columnWidth: '70%'
    }
  },
  dataLabels: { enabled: false },
  grid: {
    strokeDashArray: 0,
    yaxis: { lines: { show: true } },
    xaxis: { lines: { show: false } }
  },
  xaxis: {
    categories: categories.value,
    axisBorder: { show: false },
    axisTicks: { show: false },
    labels: { style: { fontSize: '11px' } }
  },
  yaxis: {
    min: 0,
    tickAmount: 2,
    labels: {
      formatter: (value: number) => formatNumber(Math.round(value)),
      style: { fontSize: '11px' }
    }
  },
  tooltip: {
    y: {
      formatter: (value: number) =>
        `${formatNumber(value)} ${value <= 1 ? t('pages.settings.apiKeys.usage.call') : t('pages.settings.apiKeys.usage.calls')}`
    },
    x: {
      formatter: (value: string, opts: { dataPointIndex: number }) =>
        range.value.dates[opts.dataPointIndex]?.format('ddd DD/MM') ?? value
    }
  }
}))
</script>

<template>
  <BaseModal
    :is-open="open"
    :heading-title="t('pages.settings.apiKeys.usage.title', { name: token?.name })"
    size="2xl"
    :cancel="t('pages.settings.apiKeys.usage.close')"
    @close="emit('update:open', false)"
  >
    <template #customHeadingTitle>
      <div class="flex flex-col gap-1">
        <p class="text-base font-semibold text-foreground">
          {{ t('pages.settings.apiKeys.usage.title', { name: token?.name }) }}
        </p>
        <div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
          <span class="font-mono text-xs">{{ token?.prefix }}</span>
          <span>{{ t('pages.settings.apiKeys.usage.lastUsedAt', { date: lastUsedLabel }) }}</span>
        </div>
      </div>
    </template>

    <AppSkeleton v-if="isLoading" type="tableCell" />

    <template v-else-if="neverUsed">
      <div class="flex flex-col gap-4 rounded-lg bg-muted p-6">
        <div class="flex items-start gap-3">
          <span class="flex size-10 shrink-0 items-center justify-center rounded-full border bg-background">
            <PlugZap class="size-5" />
          </span>
          <div class="flex flex-col gap-1">
            <p class="text-[15px] font-semibold text-foreground">
              {{ t('pages.settings.apiKeys.usage.neverUsed.title') }}
            </p>
            <p class="text-sm text-muted-foreground">{{ t('pages.settings.apiKeys.usage.neverUsed.description') }}</p>
          </div>
        </div>

        <div class="relative rounded-md bg-[#2a0122] px-4 py-3.5 pr-24">
          <pre class="m-0 font-mono text-xs leading-5 whitespace-pre-wrap break-all text-white">{{ snippet }}</pre>
          <Button
            variant="outline"
            size="sm"
            class="absolute top-2.5 right-2.5 border-white/20 bg-white/10 text-white hover:bg-white/20"
            @click="copySnippet"
          >
            <Check v-if="snippetCopied" />
            <Copy v-else />
            {{
              snippetCopied
                ? t('pages.settings.apiKeys.usage.snippetCopied')
                : t('pages.settings.apiKeys.usage.snippetCopy')
            }}
          </Button>
        </div>

        <a
          :href="API_DOC_URL"
          target="_blank"
          rel="noopener"
          class="inline-flex w-fit items-center gap-1.5 text-sm font-medium"
        >
          {{ t('pages.settings.apiKeys.usage.neverUsed.guideLink') }}
          <ExternalLink class="size-3.5" />
        </a>
      </div>
    </template>

    <template v-else>
      <BaseTab v-model="period" :tabs="periodTabs" disable-router class="mb-4" />
      <p class="-mt-2 mb-4 text-xs text-muted-foreground">{{ range.rangeLabel }}</p>

      <div class="grid grid-cols-3 gap-3">
        <div class="flex flex-col gap-1 rounded-md border bg-muted px-4 py-3">
          <span class="text-xs text-muted-foreground">{{ t('pages.settings.apiKeys.usage.total') }}</span>
          <span data-testid="usage-total" class="text-lg font-semibold tabular-nums text-foreground">{{
            formatNumber(total)
          }}</span>
          <span v-if="hasDelta" class="inline-flex items-center gap-1 text-xs" :class="deltaClass">
            <component :is="deltaIcon" class="size-3" />
            {{ deltaLabel }}
          </span>
        </div>
        <div class="flex flex-col gap-1 rounded-md border bg-muted px-4 py-3">
          <span class="text-xs text-muted-foreground">{{ t('pages.settings.apiKeys.usage.average') }}</span>
          <span data-testid="usage-average" class="text-lg font-semibold tabular-nums text-foreground">{{
            formatNumber(average)
          }}</span>
        </div>
        <div class="flex flex-col gap-1 rounded-md border bg-muted px-4 py-3">
          <span class="text-xs text-muted-foreground">{{ t('pages.settings.apiKeys.usage.peak') }}</span>
          <span data-testid="usage-peak" class="text-lg font-semibold tabular-nums text-foreground">{{
            peakLabel
          }}</span>
        </div>
      </div>

      <div v-if="hasData" class="pt-4">
        <apexchart type="bar" :height="chartOptions.chart.height" :options="chartOptions" :series="series" />
      </div>

      <div
        v-else
        data-testid="usage-empty-period"
        class="mt-4 flex min-h-[200px] flex-col items-center justify-center gap-2 rounded-lg bg-muted p-6 text-center"
      >
        <ChartColumn class="size-5 text-muted-foreground" />
        <p class="text-sm font-medium text-foreground">{{ t('pages.settings.apiKeys.usage.noData.title') }}</p>
        <p class="text-xs text-muted-foreground">{{ noDataHint }}</p>
      </div>
    </template>
  </BaseModal>
</template>
